import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { useGetAllAccountsQuery } from "../../../store/queries/account";
import {
  useCommitPatientBalanceUploadMutation,
  useCreatePatientBalanceUploadMutation,
  useGetPatientBalanceUploadByIdQuery,
  useGetPatientBalanceUploadsQuery,
  useUpdatePatientBalanceUploadRowMutation,
} from "../../../store/queries/patientBalanceUploads";
import {
  IPatientBalanceUploadListItem,
  IPatientBalanceUploadRow,
} from "../../../types/patientBalanceUpload";

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${sizer(3)};
  gap: ${sizer(2)};
  flex-wrap: wrap;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizer(2)};
  flex-wrap: wrap;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: ${sizer(4)};
  flex-wrap: wrap;
  margin-bottom: ${sizer(3)};
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;

  span:first-child {
    font-size: 0.75rem;
    text-transform: uppercase;
    opacity: 0.6;
  }

  span:last-child {
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

dayjs.extend(utc);

const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  gap: 0.1rem;
  min-width: 0;
`;

const CellLine = styled.span`
  white-space: nowrap;
`;

const MutedNote = styled.span`
  font-size: 0.75rem;
  line-height: 1.2;
  opacity: 0.6;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const money = (cents: number | null): string =>
  cents === null ? "—" : `$${(cents / 100).toFixed(2)}`;

/**
 * DOB and DOS are calendar dates, not instants: the parser stores them at UTC midnight, and the
 * rest of the app reads order dates the same way (`dayjs.utc` in DOBAuth / CreateOrder helpers).
 * Formatting them in local time renders midnight UTC as the PREVIOUS day for anyone west of
 * Greenwich, which is exactly what it did before this was fixed.
 */
const shortDate = (value: string | null): string =>
  value ? dayjs.utc(value).format("M/D/YYYY") : "—";

const ROW_STATUS_SEVERITY: Record<
  string,
  "success" | "info" | "warning" | "danger"
> = {
  Created: "success",
  Pending: "info",
  Skipped: "warning",
  Excluded: "warning",
  Invalid: "danger",
};

/**
 * CLE-2704 — replaces the Google Sheet + Postman flow for creating patient-responsibility orders.
 *
 * Deliberately three steps: pick the hospital, upload, then review. The hospital comes FIRST
 * because its PR discount changes every balance and therefore every payment plan — reviewing
 * before choosing would mean approving numbers that then change.
 */
function PatientBalanceUploadsTable() {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const [accountId, setAccountId] = useState<string>("");
  const [openUploadId, setOpenUploadId] = useState<string | null>(null);

  const { data: accounts } = useGetAllAccountsQuery();
  const { data: uploads, isFetching } = useGetPatientBalanceUploadsQuery({});
  const { data: detail } = useGetPatientBalanceUploadByIdQuery(
    openUploadId as string,
    { skip: !openUploadId }
  );

  const [createUpload, { isLoading: isUploading }] =
    useCreatePatientBalanceUploadMutation();
  const [updateRow] = useUpdatePatientBalanceUploadRowMutation();
  const [commitUpload, { isLoading: isCommitting }] =
    useCommitPatientBalanceUploadMutation();

  const accountOptions = useMemo(
    () =>
      (accounts?.data ?? [])
        .map((account) => ({ label: account.name, value: account.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts]
  );

  const rows = detail?.data?.rows ?? [];
  const hasPrDiscount = Number(detail?.data?.prDiscountPercent ?? 0) > 0;
  // Most accounts have patient-responsibility payment plans switched off. When they do, no plan is
  // priced and every balance is collected in full — say so up front rather than let the reviewer
  // notice an all-"Pay in full" column and wonder whether the formula broke.
  const plansEnabled =
    detail?.data?.account?.paymentPlanTypeEnabled?.patientResponsibility ===
    true;
  const pendingRows = rows.filter((row) => row.status === "Pending");
  const isCommitted = detail?.data?.status === "Committed";

  const handleUpload = async (event: { files: File[] }) => {
    const file = event.files?.[0];
    if (!file || !accountId) {
      return;
    }
    try {
      const result = await createUpload({ file, accountId }).unwrap();
      fileUploadRef.current?.clear();
      setOpenUploadId(result.data.id);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Upload failed",
        detail: error?.data?.message ?? "Could not parse the file",
        life: 6000,
      });
    }
  };

  const handleMonthsChange = async (
    row: IPatientBalanceUploadRow,
    months: number | null
  ) => {
    if (!openUploadId || !months) {
      return;
    }
    try {
      await updateRow({
        uploadId: openUploadId,
        rowId: row.id,
        months,
      }).unwrap();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Could not update",
        detail: error?.data?.message ?? "Update failed",
        life: 5000,
      });
    }
  };

  const toggleExcluded = async (row: IPatientBalanceUploadRow) => {
    if (!openUploadId) {
      return;
    }
    await updateRow({
      uploadId: openUploadId,
      rowId: row.id,
      status: row.status === "Excluded" ? "Pending" : "Excluded",
    });
  };

  const confirmCommit = () => {
    if (!openUploadId) {
      return;
    }
    confirmDialog({
      header: "Create orders and notify patients",
      // Spelled out because this is irreversible and goes to real patients.
      message: `This creates ${
        pendingRows.length
      } patient responsibility order${
        pendingRows.length === 1 ? "" : "s"
      } and texts each patient immediately. It cannot be undone, and this upload can only be committed once.`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: `Create ${pendingRows.length} orders`,
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          const result = await commitUpload(openUploadId).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Orders created",
            detail: `${result.data.ordersCreated} of ${result.data.totalEligible} created`,
            life: 6000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: "error",
            summary: "Commit failed",
            detail: error?.data?.message ?? "Could not create orders",
            life: 6000,
          });
        }
      },
    });
  };

  const uploadStatusBody = (item: IPatientBalanceUploadListItem) => (
    <Tag
      value={item.status}
      severity={item.status === "Committed" ? "success" : "info"}
    />
  );

  const uploadActionBody = (item: IPatientBalanceUploadListItem) => (
    <Button
      label={item.status === "Committed" ? "View" : "Review"}
      className="p-button-text"
      onClick={() => setOpenUploadId(item.id)}
    />
  );

  const patientBody = (row: IPatientBalanceUploadRow) => (
    <CellStack>
      <CellLine>
        {row.firstName} {row.lastName}
      </CellLine>
      {row.sourceRowNumbers.length > 1 && (
        <MutedNote
          title={`Spreadsheet rows ${row.sourceRowNumbers.join(", ")}`}
        >
          merged {row.sourceRowNumbers.length} rows
        </MutedNote>
      )}
    </CellStack>
  );

  const rowStatusBody = (row: IPatientBalanceUploadRow) => (
    <CellStack>
      <Tag
        value={row.status}
        severity={ROW_STATUS_SEVERITY[row.status] ?? "info"}
      />
      {!!row.validationErrors?.length && (
        <MutedNote title={row.validationErrors.join("; ")}>
          {row.validationErrors.join("; ")}
        </MutedNote>
      )}
    </CellStack>
  );

  const rowActionBody = (row: IPatientBalanceUploadRow) => {
    if (isCommitted || row.status === "Invalid") {
      return null;
    }
    return (
      <Button
        className="p-button-text p-button-sm"
        label={row.status === "Excluded" ? "Include" : "Exclude"}
        onClick={() => toggleExcluded(row)}
      />
    );
  };

  const planTemplate = (row: IPatientBalanceUploadRow) => {
    if (!row.months || !row.monthlyInCents) {
      // Ani's rule: under $200 no plan is offered at all. Spelled out so an empty Months
      // column reads as a deliberate outcome rather than something that failed to compute.
      return (
        <CellStack>
          <CellLine>Pay in full</CellLine>
          <MutedNote>under $200</MutedNote>
        </CellStack>
      );
    }
    // Balances rarely divide evenly; the leftover cents are collected on the first payment.
    const remainder = row.balanceInCents - row.monthlyInCents * row.months;
    return (
      <CellStack>
        <CellLine>
          {row.months} × {money(row.monthlyInCents)}
        </CellLine>
        {remainder > 0 && (
          <MutedNote>first {money(row.monthlyInCents + remainder)}</MutedNote>
        )}
      </CellStack>
    );
  };

  const monthsEditor = (row: IPatientBalanceUploadRow) => {
    if (isCommitted || !row.months) {
      return <MutedNote>—</MutedNote>;
    }
    return (
      <InputNumber
        value={row.months}
        onValueChange={(e) => handleMonthsChange(row, e.value ?? null)}
        min={1}
        max={60}
        inputStyle={{ width: "3.5rem", textAlign: "center" }}
      />
    );
  };

  return (
    <div>
      <Toast ref={toast} />

      <FlexWrapper>
        <UploadRow>
          <Dropdown
            value={accountId}
            options={accountOptions}
            onChange={(e) => setAccountId(e.value)}
            placeholder="1. Choose hospital"
            filter
            style={{ minWidth: "18rem" }}
          />
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            name="file"
            accept=".xlsx,.xls,.csv"
            chooseLabel={isUploading ? "Uploading…" : "2. Upload balance file"}
            customUpload
            auto
            disabled={!accountId || isUploading}
            uploadHandler={handleUpload}
          />
        </UploadRow>
      </FlexWrapper>

      {!accountId && (
        <Message
          severity="info"
          text="Choose a hospital first — its patient responsibility discount is applied to every balance, so it determines the payment plans you review."
        />
      )}

      <DataTable
        value={uploads?.data ?? []}
        loading={isFetching}
        paginator
        rows={10}
        emptyMessage="No uploads yet"
      >
        <Column
          field="filename"
          header="File"
          align="left"
          alignHeader="left"
        />
        <Column
          header="Hospital"
          body={(item: IPatientBalanceUploadListItem) =>
            item.account?.name ?? "—"
          }
          align="left"
          alignHeader="left"
        />
        <Column
          header="Status"
          body={uploadStatusBody}
          align="center"
          alignHeader="center"
          style={{ width: "8rem" }}
        />
        <Column
          header="Rows"
          body={(item) => `${item.mergedRowCount} of ${item.sourceRowCount}`}
          align="center"
          alignHeader="center"
          style={{ width: "8rem" }}
        />
        <Column
          field="ordersCreated"
          header="Orders created"
          align="center"
          alignHeader="center"
          style={{ width: "9rem" }}
        />
        <Column
          header="Uploaded"
          body={(item) => shortDate(item.createdAt)}
          align="center"
          alignHeader="center"
          style={{ width: "8rem" }}
        />
        <Column
          header=""
          body={uploadActionBody}
          align="right"
          alignHeader="right"
          style={{ width: "7rem" }}
        />
      </DataTable>

      <Dialog
        header={`3. Review — ${detail?.data?.filename ?? ""}`}
        visible={!!openUploadId}
        style={{ width: "95vw", maxWidth: "1500px" }}
        onHide={() => setOpenUploadId(null)}
      >
        <SummaryRow>
          <Summary>
            <span>Hospital</span>
            <span>{detail?.data?.account?.name ?? "—"}</span>
          </Summary>
          <Summary>
            <span>PR discount</span>
            <span>{detail?.data?.prDiscountPercent ?? "0"}%</span>
          </Summary>
          <Summary>
            <span>Rows in file</span>
            <span>{detail?.data?.sourceRowCount ?? 0}</span>
          </Summary>
          <Summary>
            <span>After merging</span>
            <span>{detail?.data?.mergedRowCount ?? 0}</span>
          </Summary>
          <Summary>
            <span>Will be created</span>
            <span>{pendingRows.length}</span>
          </Summary>
        </SummaryRow>

        {/*
          Every column sets BOTH `align` and `alignHeader`. PrimeReact's `align` only moves the
          body cells, so a column with `align="right"` and no `alignHeader` renders its header
          left and its values right — which is what made DOS and the money columns look offset.
        */}
        {/*
          Every column sets BOTH `align` and `alignHeader`: PrimeReact's `align` only moves the
          body cells, so setting it alone renders the header and its values out of line.
          Provenance lives inside the Patient cell and validation errors inside Status, which
          keeps two columns off the width budget and the table free of a horizontal scrollbar.
        */}
        {!!detail?.data && !plansEnabled && (
          <Message
            severity="warn"
            style={{ marginBottom: "1rem" }}
            text="Payment plans are not enabled for this hospital, so every balance here will be collected in full. Turn on patient responsibility payment plans for the account first if this campaign is meant to offer them."
          />
        )}

        <DataTable value={rows} paginator rows={25}>
          <Column
            header="Patient"
            body={patientBody}
            align="left"
            alignHeader="left"
            style={{ width: "11rem" }}
          />
          <Column
            header="DOB"
            body={(row) => shortDate(row.dateOfBirth)}
            align="center"
            alignHeader="center"
            style={{ width: "6.5rem" }}
          />
          <Column
            field="phoneNumber"
            header="Phone"
            align="center"
            alignHeader="center"
            style={{ width: "8rem" }}
          />
          <Column
            header="DOS"
            body={(row) => shortDate(row.dateOfService)}
            align="center"
            alignHeader="center"
            style={{ width: "6.5rem" }}
          />
          <Column
            header="CPT"
            body={(row) => row.cptCodes.join(", ")}
            align="center"
            alignHeader="center"
            style={{ width: "5.5rem" }}
          />
          {hasPrDiscount && (
            <Column
              header="File amount"
              body={(row) => money(row.rawAmountInCents)}
              align="right"
              alignHeader="right"
              style={{ width: "7rem" }}
            />
          )}
          <Column
            header="Balance"
            body={(row) => money(row.balanceInCents)}
            align="right"
            alignHeader="right"
            style={{ width: "7rem" }}
          />
          <Column
            header="Months"
            body={monthsEditor}
            align="center"
            alignHeader="center"
            style={{ width: "5.5rem" }}
          />
          <Column
            header="Plan"
            body={planTemplate}
            align="left"
            alignHeader="left"
            style={{ width: "9rem" }}
          />
          <Column
            header="Status"
            body={rowStatusBody}
            align="center"
            alignHeader="center"
            style={{ width: "8rem" }}
          />
          <Column
            header=""
            body={rowActionBody}
            align="right"
            alignHeader="right"
            style={{ width: "5.5rem" }}
          />
        </DataTable>

        <FlexWrapper style={{ marginTop: "1rem", justifyContent: "flex-end" }}>
          {isCommitted ? (
            <Message
              severity="success"
              text={`Committed — ${
                detail?.data?.ordersCreated ?? 0
              } orders created on ${shortDate(
                detail?.data?.committedAt ?? null
              )}`}
            />
          ) : (
            <Button
              label={`Create ${pendingRows.length} orders and notify patients`}
              icon="pi pi-send"
              disabled={!pendingRows.length || isCommitting}
              loading={isCommitting}
              onClick={confirmCommit}
            />
          )}
        </FlexWrapper>
      </Dialog>
    </div>
  );
}

export default PatientBalanceUploadsTable;
